import styled from '@emotion/styled';
import { Icon, tokens, Tooltip, Table } from '@hotjar/hotsauce';
import { formatPercentage, useTranslation } from '@hotjar/translations';
import { css } from '@emotion/react';
import { Blur } from '@hotjar/webapp/components/Blur/Blur';
import { useNavigation } from '@hotjar/navigation';
import type { i18n } from 'i18next';
import { getQuestionLabelAndIcon } from '../../utils';
import type { BreakdownQuestion } from '../../../../domain/Surveys/Performance/Breakdown';

const Lead = styled.div`
  max-width: 564px;
`;

const Heading = styled.h1`
  display: block;
  margin-bottom: ${tokens.spacer.x1};
  ${tokens.text('header5')}
`;

const Desc = styled.p`
  margin-bottom: ${tokens.spacer.x2};
  ${tokens.text('caption')}
`;

const BLUR_BREAKDOWN = 1234;

const numberFormat = new Intl.NumberFormat('en-EN');

function formatPercent(percentage: number, i18n: i18n): string {
  return formatPercentage(percentage * 100, i18n);
}

function formatCount(count: number, index: number, isRestricted: boolean): string | JSX.Element {
  if (index === 0) return '--';
  if (isRestricted) return <Blur>{numberFormat.format(BLUR_BREAKDOWN)}</Blur>;
  return numberFormat.format(count);
}

type AbandonmentRateProps = {
  question: BreakdownQuestion;
  index: number;
  isRestricted: boolean;
  i18n: i18n;
};

function getAbandonmentRate({
  question,
  index,
  isRestricted,
  i18n,
}: AbandonmentRateProps): string | JSX.Element {
  if (index === 0) return '--';
  if (isRestricted) return <Blur>{numberFormat.format(BLUR_BREAKDOWN)}</Blur>;
  if (question.impressions_count === 0) return '--';
  return formatPercent(question.drop_off_count / question.impressions_count, i18n);
}
type SurveyBreakdownProps = {
  isRestricted: boolean;
  questions: BreakdownQuestion[] | null;
};

export const SurveyBreakdown = ({
  isRestricted,
  questions,
}: SurveyBreakdownProps): JSX.Element | null => {
  const { t, i18n } = useTranslation('feedback', { keyPrefix: 'surveys.performance' });
  const { t: tQuestionTypes } = useTranslation('feedback', {
    keyPrefix: 'survey.question-types',
  });

  const { topBarHeight: topNavHeight } = useNavigation();

  return (
    <>
      <Lead>
        <Heading>{t('breakdown.title')}</Heading>
        <Desc>{t('breakdown.description')}</Desc>
      </Lead>
      {questions && (
        <Table caption={t('breakdown.table.caption')} tableName={t('breakdown.table.name')}>
          <Table.Head>
            <Table.HeadRow stickyOffset={topNavHeight}>
              <Table.HeadCell>{t('breakdown.table.type')}</Table.HeadCell>
              <Table.HeadCell>{t('breakdown.table.question')}</Table.HeadCell>
              <Table.HeadCell
                css={css`
                  min-width: 116px;
                `}
              >
                {t('breakdown.table.impressions')}
              </Table.HeadCell>
              <Table.HeadCell
                css={css`
                  min-width: 104px;
                `}
              >
                {t('breakdown.table.drop-offs')}
              </Table.HeadCell>
              <Table.HeadCell
                css={css`
                  min-width: 132px;
                `}
              >
                {t('breakdown.table.drop-off-rate')}
              </Table.HeadCell>
            </Table.HeadRow>
          </Table.Head>
          <Table.Body>
            {questions.map((question, index) => {
              const { icon, label } = getQuestionLabelAndIcon(question.type);
              return (
                <Table.Row key={question.uuid}>
                  <Table.Cell>
                    <Tooltip text={tQuestionTypes(label)}>
                      <Icon icon={icon} description={tQuestionTypes(label)} />
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>{question.text}</Table.Cell>
                  <Table.Cell>
                    {formatCount(question.impressions_count, index, isRestricted)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatCount(question.drop_off_count, index, isRestricted)}
                  </Table.Cell>
                  <Table.Cell>
                    {getAbandonmentRate({ question, index, isRestricted, i18n })}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </>
  );
};
